import { BaseGameRecorder, generateId } from '@bn-games/history-core';
import { DEFAULT_VARIANT } from '../game/rules/index.js';
import type { Color } from '../game/types.js';
import { stateFromSnapshot, type ChessSnapshot } from '../network/protocol.js';
import { ActiveGameStore } from './ActiveGameStore.js';
import { buildRecord } from './record.js';
import { HISTORY_SCHEMA_VERSION } from './types.js';
import type { ActiveSession, GameRecord, RecordedClocks } from './types.js';

/** Our seat at the table, as the room reports it when a snapshot arrives. */
export interface OnlineSeat {
  readonly roomCode: string;
  readonly color: Color;
  readonly localName: string;
  readonly opponentName: string;
}

/** Whether `snapshot` is the game `session` holds, at the same point or further on. */
function continues(session: ActiveSession, snapshot: ChessSnapshot, roomCode: string): boolean {
  return session.roomCode === roomCode && session.startFen === snapshot.startFen &&
    session.moves.length <= snapshot.moves.length && session.moves.every((uci, i) => uci === snapshot.moves[i]);
}

/**
 * A reading is trusted only when this snapshot is the one that delivered the
 * move: any later one has the waiting side's clock already running down. So
 * it must add exactly one move, and we must have been `watching` — the first
 * snapshot after a reload arrives some unknown time after the move it shows.
 */
function clocksThrough(
  known: readonly (RecordedClocks | null)[],
  snapshot: ChessSnapshot,
  watching: boolean,
): (RecordedClocks | null)[] {
  const out = known.slice(0, snapshot.moves.length);
  const delivered = watching && snapshot.moves.length === known.length + 1;
  while (out.length < snapshot.moves.length) out.push(null);
  if (delivered && snapshot.clocks !== null) out[out.length - 1] = { w: snapshot.clocks.w, b: snapshot.clocks.b };
  return out;
}

/**
 * Records an online game from the server's snapshots — never from what this
 * client showed ahead of the echo. The session it keeps doubles as the
 * "Resume online" label and survives a reload.
 *
 * A finished game is reported exactly once, and only if it was seen in
 * progress (now, or before a reload): a snapshot that is already over with no
 * session behind it is a game recorded earlier or never watched, so reopening
 * a finished room adds nothing.
 */
export class GameRecorder extends BaseGameRecorder<ActiveSession> {
  constructor(private readonly now: () => number = Date.now) {
    super(ActiveGameStore);
  }

  /** Feed every snapshot. Returns the record the first time a tracked game is over. */
  observe(snapshot: ChessSnapshot, seat: OnlineSeat): GameRecord | null {
    // Unreadable (a newer server, a corrupted frame): keep what we have, as the match does.
    const state = stateFromSnapshot(snapshot);
    if (state === null) return null;
    const session = this.track(snapshot, seat);
    if (snapshot.outcome === null || session === null) return null;
    this.discard();
    const white = seat.color === 'w' ? seat.localName : seat.opponentName;
    const black = seat.color === 'w' ? seat.opponentName : seat.localName;
    return buildRecord({
      id: session.id,
      seed: {
        mode: 'online', variant: DEFAULT_VARIANT, difficulty: null, playerWhite: white, playerBlack: black,
        localColor: seat.color, roomCode: seat.roomCode, createdAt: session.createdAt,
      },
      state,
      clocks: session.clocks,
      finishedAt: this.now(),
    });
  }

  /** Bring the session up to `snapshot`; returns it, or null when this is not a game we follow. */
  private track(snapshot: ChessSnapshot, seat: OnlineSeat): ActiveSession | null {
    const watching = this.session !== null;
    // After a reload the game in progress is the one saved before it.
    const held = this.session ?? this.store.peek();
    const current = held !== null && continues(held, snapshot, seat.roomCode) ? held : null;
    if (current === null && snapshot.outcome !== null) {
      // Over before we ever saw it running: nothing to record, and nothing left to resume
      // here. A game saved for another room is not ours to drop.
      if (held === null || held.roomCode === seat.roomCode) this.discard();
      return null;
    }
    const now = this.now();
    const session: ActiveSession = {
      version: HISTORY_SCHEMA_VERSION,
      mode: 'online',
      id: current?.id ?? generateId(),
      createdAt: current?.createdAt ?? now,
      roomCode: seat.roomCode,
      color: seat.color,
      localName: seat.localName,
      opponentName: seat.opponentName,
      startFen: snapshot.startFen,
      moves: snapshot.moves,
      clocks: clocksThrough(current?.clocks ?? [], snapshot, watching),
      savedAt: now,
    };
    this.setSession(session);
    return session;
  }
}
